import {
  Spin, Button, Card, Form, Select, Space, Table, Input, Tag, Switch,
  ConfigProvider, Popover, Typography, Pagination, message,
  Row, Col, Modal, Tooltip,
} from 'antd';
import React, { useEffect, useRef, useState } from 'react';
import App from '../LayoutMobile';
import {
  DeleteOutlined, EditOutlined, EyeOutlined,
  ExclamationCircleOutlined,
  MedicineBoxOutlined, HeartOutlined, BankOutlined, PlusOutlined,
  HolderOutlined,
  // Icon library imports
  VideoCameraOutlined, ExperimentOutlined, SafetyOutlined, TeamOutlined,
  UserOutlined, GlobalOutlined, CalendarOutlined, ClockCircleOutlined,
  PhoneOutlined, FileTextOutlined, StarOutlined, SmileOutlined,
  CompassOutlined, CustomerServiceOutlined, ReconciliationOutlined,
  CrownOutlined, ThunderboltOutlined, TrophyOutlined,
  SecurityScanOutlined, AuditOutlined, AlertOutlined, LikeOutlined,
  AppstoreOutlined, SearchOutlined, CloseOutlined,
  // Service-specific icons
  SkinOutlined, ZoomInOutlined, WomanOutlined, ManOutlined,
  FireOutlined, BulbOutlined, ApartmentOutlined,
  SafetyCertificateOutlined, ShareAltOutlined,
  AudioOutlined, EnvironmentOutlined, IdcardOutlined, DashboardOutlined,
  CoffeeOutlined, RestOutlined, BugOutlined, ScissorOutlined,
  PlusSquareOutlined, HomeOutlined, ShopOutlined, LineChartOutlined, AimOutlined,
  PoweroffOutlined,
} from '@ant-design/icons';
import { DndContext, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { arrayMove, SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import axios from 'axios';
import { Config } from '../Common/Config';
import './NewProductCategory.css';

const { Option } = Select;
const { Title } = Typography;

// ─── Category Type Config ─────────────────────────────────────────────────────
const CATEGORY_TYPE_OPTIONS = [
  {
    value: 'gp',
    label: 'GP (General Practice)',
    color: '#008385',
    bg: 'rgba(0,131,133,0.10)',
    icon: <MedicineBoxOutlined />,
  },
  {
    value: 'd2c',
    label: 'D2C',
    color: '#1677ff',
    bg: 'rgba(22,119,255,0.10)',
    icon: <HeartOutlined />,
  },
  {
    value: 'corporate',
    label: 'Corporate',
    color: '#6b45c8',
    bg: 'rgba(107,69,200,0.10)',
    icon: <BankOutlined />,
  },
  {
    value: 'video',
    label: 'Video',
    color: '#d97706',
    bg: 'rgba(217,119,6,0.10)',
    icon: <VideoCameraOutlined />,
  },
];

const getCategoryTypeConfig = (type) =>
  CATEGORY_TYPE_OPTIONS.find((o) => o.value === type) || { value: type, label: type, color: '#6b7280', bg: '#f3f4f6', icon: <AppstoreOutlined /> };

// ─── Available icon library (curated 24-icon set) ────────────────────────────
const AVAILABLE_ICONS = [
  { name: 'MedicineBoxOutlined', label: 'Medical Box', C: MedicineBoxOutlined },
  { name: 'HeartOutlined', label: 'Heart', C: HeartOutlined },
  { name: 'BankOutlined', label: 'Building', C: BankOutlined },
  { name: 'VideoCameraOutlined', label: 'Video Camera', C: VideoCameraOutlined },
  { name: 'ExperimentOutlined', label: 'Lab / Test Tube', C: ExperimentOutlined },
  { name: 'SafetyOutlined', label: 'Safety Shield', C: SafetyOutlined },
  { name: 'TeamOutlined', label: 'Team', C: TeamOutlined },
  { name: 'UserOutlined', label: 'Person', C: UserOutlined },
  { name: 'GlobalOutlined', label: 'Global', C: GlobalOutlined },
  { name: 'CalendarOutlined', label: 'Calendar', C: CalendarOutlined },
  { name: 'ClockCircleOutlined', label: 'Clock', C: ClockCircleOutlined },
  { name: 'PhoneOutlined', label: 'Phone', C: PhoneOutlined },
  { name: 'FileTextOutlined', label: 'Document', C: FileTextOutlined },
  { name: 'StarOutlined', label: 'Star', C: StarOutlined },
  { name: 'SmileOutlined', label: 'Wellness', C: SmileOutlined },
  { name: 'CompassOutlined', label: 'Compass', C: CompassOutlined },
  { name: 'CustomerServiceOutlined', label: 'Support', C: CustomerServiceOutlined },
  { name: 'ReconciliationOutlined', label: 'Checklist', C: ReconciliationOutlined },
  { name: 'CrownOutlined', label: 'Premium', C: CrownOutlined },
  { name: 'ThunderboltOutlined', label: 'Quick / Fast', C: ThunderboltOutlined },
  { name: 'TrophyOutlined', label: 'Achievement', C: TrophyOutlined },
  { name: 'SecurityScanOutlined', label: 'Security Scan', C: SecurityScanOutlined },
  { name: 'AuditOutlined', label: 'Audit / Check', C: AuditOutlined },
  { name: 'AlertOutlined', label: 'Alert / Emergency', C: AlertOutlined },
  { name: 'LikeOutlined', label: 'Like / Recommend', C: LikeOutlined },
  // Service-specific
  { name: 'SkinOutlined', label: 'Dermatology / Skin', C: SkinOutlined },
  { name: 'ZoomInOutlined', label: 'Mole Checks', C: ZoomInOutlined },
  { name: 'WomanOutlined', label: "Gynaecology / Women's", C: WomanOutlined },
  { name: 'ManOutlined', label: "Men's Health", C: ManOutlined },
  { name: 'FireOutlined', label: 'Menopause', C: FireOutlined },
  { name: 'BulbOutlined', label: 'Mental Health', C: BulbOutlined },
  { name: 'ApartmentOutlined', label: 'Orthopaedics', C: ApartmentOutlined },
  { name: 'SafetyCertificateOutlined', label: 'STI / STD Testing', C: SafetyCertificateOutlined },
  { name: 'ShareAltOutlined', label: 'Referrals', C: ShareAltOutlined },
  { name: 'EyeOutlined', label: 'Vision / Eye Care', C: EyeOutlined },
  { name: 'AudioOutlined', label: 'Hearing / ENT', C: AudioOutlined },
  { name: 'EnvironmentOutlined', label: 'Travel Clinic', C: EnvironmentOutlined },
  { name: 'IdcardOutlined', label: 'Visa Medicals', C: IdcardOutlined },
  { name: 'DashboardOutlined', label: 'Health Metrics / BP', C: DashboardOutlined },
  { name: 'CoffeeOutlined', label: 'Nutrition / Dietitian', C: CoffeeOutlined },
  { name: 'RestOutlined', label: 'Sleep Clinic', C: RestOutlined },
  { name: 'BugOutlined', label: 'Infections / Bugs', C: BugOutlined },
  { name: 'ScissorOutlined', label: 'Minor Surgery', C: ScissorOutlined },
  { name: 'PlusSquareOutlined', label: 'First Aid / General', C: PlusSquareOutlined },
  { name: 'HomeOutlined', label: 'Home Visit', C: HomeOutlined },
  { name: 'ShopOutlined', label: 'Pharmacy / Prescript', C: ShopOutlined },
  { name: 'LineChartOutlined', label: 'Blood Tests / Track', C: LineChartOutlined },
  { name: 'AimOutlined', label: 'Vaccinations / Target', C: AimOutlined },
];

// ─── Dynamic icon renderer ────────────────────────────────────────────────────
const renderDynamicIcon = (iconName, style = {}) => {
  const found = AVAILABLE_ICONS.find((ic) => ic.name === iconName);
  if (!found) return null;
  const { C: IconComp } = found;
  return <IconComp style={style} />;
};

// ─── Icon Picker Modal ────────────────────────────────────────────────────────
const IconPickerModal = ({ open, onClose, onSelect, value }) => {
  const [search, setSearch] = useState('');
  const filtered = AVAILABLE_ICONS.filter(
    (ic) =>
      ic.label.toLowerCase().includes(search.toLowerCase()) ||
      ic.name.toLowerCase().includes(search.toLowerCase()),
  );
  return (
    <Modal
      title={<span><AppstoreOutlined style={{ marginRight: 8, color: '#008385' }} />Choose Icon</span>}
      open={open}
      onCancel={onClose}
      footer={null}
      width={520}
      destroyOnClose
    >
      <Input
        prefix={<SearchOutlined style={{ color: '#aaa' }} />}
        placeholder="Search icons…"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        allowClear
        style={{ marginBottom: 16 }}
      />
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(6, 1fr)',
          gap: 8,
          maxHeight: 380,
          overflowY: 'auto',
          paddingRight: 4,
        }}
      >
        {filtered.map((ic) => {
          const { C: Ico } = ic;
          const isSelected = value === ic.name;
          return (
            <Tooltip key={ic.name} title={ic.label} placement="top">
              <div
                onClick={() => { onSelect(ic.name); onClose(); }}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 4,
                  padding: '10px 4px 6px',
                  borderRadius: 8,
                  cursor: 'pointer',
                  border: isSelected ? '2px solid #008385' : '2px solid transparent',
                  background: isSelected ? 'rgba(0,131,133,0.08)' : '#f5f5f5',
                  transition: 'all 0.15s',
                }}
              >
                <Ico style={{ fontSize: 22, color: isSelected ? '#008385' : '#4b5563' }} />
                <span style={{ fontSize: 9, color: '#6b7280', textAlign: 'center', lineHeight: 1.2 }}>
                  {ic.label}
                </span>
              </div>
            </Tooltip>
          );
        })}
        {filtered.length === 0 && (
          <div style={{ gridColumn: '1/-1', textAlign: 'center', color: '#aaa', padding: 24 }}>
            No icons found
          </div>
        )}
      </div>
    </Modal>
  );
};

// ─── Type Tag component ───────────────────────────────────────────────────────
const TypeTag = ({ type }) => {
  const cfg = getCategoryTypeConfig(type);
  if (!cfg)
    return (
      <span style={{ color: '#9ca3af', fontSize: 13 }}>{type || '—'}</span>
    );
  return (
    <Tag
      icon={cfg.icon}
      style={{
        background: cfg.bg,
        borderColor: cfg.color + '40',
        color: cfg.color,
        borderRadius: 20,
        fontWeight: 600,
        fontSize: 12,
        padding: '2px 10px',
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
      }}
    >
      {cfg.label}
    </Tag>
  );
};

// ─── Drag Handle ─────────────────────────────────────────────────────────────
const DragHandle = () => (
  <HolderOutlined
    style={{ cursor: 'grab', color: '#bbb', fontSize: 16 }}
  />
);

// ─── Sortable Row (wraps each <tr> for DnD) ───────────────────────────────────
const SortableRow = (props) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: props['data-row-key'] });

  const style = {
    ...props.style,
    transform: CSS.Transform.toString(transform),
    transition,
    cursor: 'move',
    ...(isDragging ? { position: 'relative', zIndex: 9999, background: '#f0fafa' } : {}),
  };

  return (
    <tr
      {...props}
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
    />
  );
};

// ─── Component ────────────────────────────────────────────────────────────────
const NewProductCategory = () => {
  const [form] = Form.useForm();
  const [selectedTypeValue, setSelectedTypeValue] = useState(null);
  const [userDataSet, setuserDataSet] = useState({});
  const [AllClinicianData, setAllClinicianData] = useState([]);
  const [loader, setloader] = useState(false);
  const [deleteLoader, setDeleteLoader] = useState(false);
  const [messageApi, contextHolder] = message.useMessage();
  const [CurrentPage, setCurrentPage] = useState(1);
  const [isUserCreateEnable, setisUserCreateEnable] = useState(false);
  const [showAddButton, setshowAddButton] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formMode, setFormMode] = useState('edit');
  const [categoryTypeMetaData, setCategoryTypeMetaData] = useState([]);
  const isViewMode = formMode === 'view';

  // icon picker state
  const [selectedIconName, setSelectedIconName] = useState(null);
  const [iconPickerOpen, setIconPickerOpen] = useState(false);

  const [showCategory, setshowCategory] = useState(null);
  const [draggedData, setDraggedData] = useState([]);
  const hasFetched = useRef(false);

  const getInitials = (name = '') => {
    const words = (name || '').trim().split(/\s+/).filter(Boolean);
    if (words.length >= 2) return (words[0][0] + words[1][0]).toUpperCase();
    if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
    return '?';
  };

  const getCategoryTypeMetaData = async () => {
    if (!localStorage.getItem('token')) return;
    try {
      const response = await axios.get(`${Config.urlLogin}/api/v1/meta-data`, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        params: { category: 'product_category_type' },
      });
      if (response?.data?.data?.data) {
        setCategoryTypeMetaData(response.data.data.data);
      }
    } catch (error) {
      console.log('error fetching category type metadata:', error);
    }
  };

  // ─── DnD sensors ─────────────────────────────────────────────────────────
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 1 } })
  );

  useEffect(() => {
    if (!hasFetched.current) {
      getAllProductData(CurrentPage);
      getCategoryTypeMetaData();
      hasFetched.current = true;
    }
  }, [CurrentPage]);

  // ─── Sync draggedData whenever the fetched list changes ──────────────────
  useEffect(() => {
    if (AllClinicianData?.data) {
      setDraggedData(AllClinicianData.data);
    }
  }, [AllClinicianData]);

  // ─── Fetch all categories ─────────────────────────────────────────────────
  const getAllProductData = async (page) => {
    if (!page) return;
    setloader(true);
    try {
      const url = `${Config.url}product-category/page/${page}`;
      const response = await axios.get(url, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });
      setAllClinicianData(response?.data?.data);

      // ── Map ALL categories by product_category_id ─────────────────────
      if (response?.data?.data?.data?.length) {
        const category = {};
        response.data.data.data.forEach((cat) => {
          if (cat?.product_category_id) {
            category[cat.product_category_id] = cat;
          }
        });
        if (Object.keys(category).length) {
          setshowCategory(category);
          localStorage.setItem('showCategory', JSON.stringify(category));
        }
      }

      hasFetched.current = false;
    } catch (error) {
      console.log('error in product-category ::::', error);
      messageApi.error({
        type: 'error',
        content:
          error.response?.data?.message ||
          error.message ||
          'Something went wrong',
      });
    } finally {
      setloader(false);
    }
  };

  // ─── Category reorder API (PUT /product-category/update-order) ────────────
  const updateCategoryOrder = async (orderedData) => {
    try {
      const order = orderedData.map((item, index) => ({
        product_category_id: item.product_category_id,
        sort_order: index + 1,
      }));

      await axios.put(
        `${Config.url}product-category/update-order`,
        { order },
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        }
      );

      // Keep sidebar localStorage in sync with the new order
      // Also write sort_order back into each category object so the
      // Appointment Type list sorts correctly via buildCategoriesFromStorage
      const newCategoryMap = {};
      orderedData.forEach((cat, index) => {
        if (cat?.product_category_id) {
          newCategoryMap[cat.product_category_id] = {
            ...cat,
            sort_order: index + 1,   // ← persist the new order into the object
          };
        }
      });
      localStorage.setItem('showCategory', JSON.stringify(newCategoryMap));
      // Notify LayoutMobile to re-render the sidebar with new order immediately
      window.dispatchEvent(new Event('categoryOrderUpdated'));

      messageApi.success({ content: 'Category order updated successfully' });
    } catch (error) {
      console.error('error in product-category/update-order:', error);
      messageApi.error({
        content:
          error.response?.data?.message ||
          error.message ||
          'Failed to update category order',
      });
    }
  };

  // ─── Drag end handler ─────────────────────────────────────────────────────
  const onDragEnd = ({ active, over }) => {
    if (active.id !== over?.id) {
      setDraggedData((prevData) => {
        const activeIndex = prevData.findIndex(
          (i) => i.product_category_id === active.id
        );
        const overIndex = prevData.findIndex(
          (i) => i.product_category_id === over?.id
        );
        const newData = arrayMove(prevData, activeIndex, overIndex);
        updateCategoryOrder(newData);
        return newData;
      });
    }
  };

  // ─── Create / Update ──────────────────────────────────────────────────────
  const onFinish = async (values, update = false) => {

    setIsSubmitting(true);

    if (!update) {
      // ── CREATE ────────────────────────────────────────────────────────────
      try {
        const payload = {
          name: values.name,
          description: values.description,
          type: values.type,
          icon_name: values.icon_name || null,
          status: 1, // new categories are active by default
        };
        await axios.post(`${Config.url}product-category`, payload, {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        });
        messageApi.success({ content: 'Product Created successfully' });
        setuserDataSet({});
        form.resetFields();
        setSelectedIconName(null);
        setFormMode('edit');
        setisUserCreateEnable(false);
        setshowAddButton(true);
        getAllProductData(CurrentPage);
      } catch (error) {
        messageApi.error({
          content:
            error.response?.data?.message ||
            error.message ||
            'Something went wrong',
        });
        console.log('create category:', error.response?.data || error.message);
      }
    } else {
      // ── UPDATE ────────────────────────────────────────────────────────────
      if (!userDataSet?.product_category_id) return;
      const url = `${Config.url}product-category/${userDataSet.product_category_id}`;
      try {
        await axios.put(
          url,
          {
            name: values.name,
            description: values.description,
            type: values.type,
            icon_name: values.icon_name || null,
            status: values.status !== undefined ? (values.status ? 1 : 0) : 1,
          },
          {
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${localStorage.getItem('token')}`,
            },
          },
        );
        messageApi.success({ content: 'Product Updated successfully' });
        setuserDataSet({});
        form.resetFields();
        setSelectedIconName(null);
        setFormMode('edit');
        setisUserCreateEnable(false);
        setshowAddButton(true);
        getAllProductData(CurrentPage);
      } catch (error) {
        messageApi.error({
          content:
            error.response?.data?.message ||
            error.message ||
            'Something went wrong',
        });
        console.log('update category:', error.response?.data || error.message);
      }
    }

    setIsSubmitting(false);
  };

  // ─── Delete (double confirm) ──────────────────────────────────────────────
  const deleteUser = async (record) => {
    const url = `${Config.url}product-category/${record.product_category_id}`;
    setDeleteLoader(true);
    try {
      await axios.delete(url, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      messageApi.success({ content: 'Product deleted successfully' });
      getAllProductData(CurrentPage);
    } catch (error) {
      console.log('deleteUser:', error.response?.data || error.message);
      messageApi.error({
        content:
          error.response?.data?.message ||
          error.message ||
          'Something went wrong',
      });
    } finally {
      setDeleteLoader(false);
    }
  };

  // ── Status toggle (Active ↔ Inactive) ────────────────────────────────────
  const updateStatus = async (record, newStatus) => {
    if (!record?.product_category_id) return;
    setloader(true);
    const url = `${Config.url}product-category/${record.product_category_id}`;
    try {
      await axios.put(
        url,
        { status: newStatus ? 1 : 0 },
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        },
      );
      messageApi.success({ content: `Category ${newStatus ? 'activated' : 'deactivated'} successfully` });
      getAllProductData(CurrentPage);
      // Update form field if currently open for this record
      if (userDataSet?.product_category_id === record.product_category_id) {
        form.setFieldValue('status', newStatus);
        setuserDataSet((prev) => ({ ...prev, status: newStatus ? 1 : 0 }));
      }
    } catch (error) {
      messageApi.error({ content: error.response?.data?.message || error.message || 'Something went wrong' });
      console.log('update status:', error.response?.data || error.message);
    } finally {
      setloader(false);
    }
  };

  const handleDeleteClick = (record) => {
    // First confirmation
    Modal.confirm({
      title: 'Delete Category',
      icon: <ExclamationCircleOutlined style={{ color: '#d9363e' }} />,
      content: `Are you sure you want to delete "${record.name}"?`,
      okText: 'Yes, Continue',
      cancelText: 'Cancel',
      okButtonProps: { danger: true },
      onOk() {
        // Second confirmation
        Modal.confirm({
          title: 'Final Confirmation — Cannot Be Undone',
          icon: <ExclamationCircleOutlined style={{ color: '#d9363e' }} />,
          content: (
            <div>
              <p>
                You are about to <strong>permanently delete</strong>{' '}
                <strong>"{record.name}"</strong>.
              </p>
              <p>This action cannot be undone. Are you absolutely sure?</p>
            </div>
          ),
          okText: 'Delete Permanently',
          cancelText: 'Cancel',
          okButtonProps: { danger: true },
          onOk: () => deleteUser(record),
        });
      },
    });
  };


  // ─── Table columns ────────────────────────────────────────────────────────
  const columns = [
    {
      key: 'sort',
      width: 44,
      render: () => <DragHandle />,
    },
    {
      title: (
        <div style={{ marginLeft: '5%', textAlign: 'end', marginRight: '33%' }}>
          ID
        </div>
      ),
      dataIndex: 'product_category_id',
      key: 'id',
      render: (_, record) => (
        <div style={{ textAlign: 'center' }}>{record.product_category_id}</div>
      ),
    },
    {
      title: (
        <div style={{ marginLeft: '5%', textAlign: 'start', marginRight: '33%' }}>
          Name
        </div>
      ),
      dataIndex: 'name',
      key: 'name',
      render: (_, record) => (
        <div style={{ marginLeft: '5%', display: 'flex', alignItems: 'center', gap: 10 }}>
          {record.icon_name && (
            <div style={{
              width: 32, height: 32, borderRadius: 8,
              background: 'rgba(0,131,133,0.08)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              border: '1px solid rgba(0,131,133,0.18)', flexShrink: 0,
            }}>
              {renderDynamicIcon(record.icon_name, { fontSize: 16, color: '#008385' })}
            </div>
          )}
          {record.name}
        </div>
      ),
    },
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      render: (_, record) => <TypeTag type={record.type} />,
    },
    {
      title: 'Status',
      key: 'status',
      render: (_, record) => {
        const isActive = record.status == 1 || record.status === true;
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }} onClick={(e) => e.stopPropagation()}>
            <Switch
              size="small"
              checked={isActive}
              onChange={(checked) => updateStatus(record, checked)}
              style={{ backgroundColor: isActive ? '#008385' : '#d9d9d9' }}
            />
            <span style={{ fontSize: 12, color: isActive ? '#008385' : '#9ca3af', fontWeight: 600 }}>
              {isActive ? 'Active' : 'Inactive'}
            </span>
          </div>
        );
      },
    },
    {
      title: '',
      key: 'action',
      render: (_, record) => (
        <div
          style={{ textAlign: 'right' }}
          onClick={(e) => e.stopPropagation()}
        >
          <Popover
            placement="left"
            trigger="click"
            content={() => content(record)}
          >
            <button
              onClick={(e) => e.stopPropagation()}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                fontWeight: 700,
                fontSize: 18,
                letterSpacing: 2,
                color: '#008385',
                padding: '0 6px',
                lineHeight: 1,
              }}
            >
              ···
            </button>
          </Popover>
        </div>
      ),
    },
  ];

  // ─── Popover action menu ──────────────────────────────────────────────────
  const content = (record) => (
    <div
      className="rule-action-popover"
      style={{ display: 'flex', flexDirection: 'column', padding: '4px 0' }}
    >
      {/* View */}
      <div
        className="rule-action-item"
        style={{ color: '#008385' }}
        onClick={(e) => {
          e.stopPropagation();
          setFormMode('view');
          setshowAddButton(false);
          setisUserCreateEnable(true);
          setuserDataSet(record);
          form.setFieldsValue(record);
          setSelectedIconName(record.icon_name || null);
          document
            .querySelector('.gpProductCreate-form')
            ?.scrollIntoView({ behavior: 'smooth' });
        }}
      >
        <EyeOutlined style={{ fontSize: 15 }} /> View
      </div>

      {/* Edit */}
      <div
        className="rule-action-item"
        style={{ color: '#008385' }}
        onClick={(e) => {
          e.stopPropagation();
          setFormMode('edit');
          setshowAddButton(false);
          setisUserCreateEnable(true);
          setuserDataSet(record);
          form.setFieldsValue(record);
          setSelectedIconName(record.icon_name || null);
          document
            .querySelector('.gpProductCreate-form')
            ?.scrollIntoView({ behavior: 'smooth' });
        }}
      >
        <EditOutlined style={{ fontSize: 15 }} /> Edit
      </div>

    </div>
  );

  // ─── Pagination label ─────────────────────────────────────────────────────
  const itemRender = (_, type, originalElement) => {
    if (type === 'prev') return <a style={{ color: '#4A4A4A' }}>Previous</a>;
    if (type === 'next') return <a style={{ color: '#4A4A4A' }}>Next</a>;
    return originalElement;
  };

  // ─── Row click → view ─────────────────────────────────────────────────────
  const handleTableRowClick = (record) => {
    setFormMode('view');
    setisUserCreateEnable(true);
    setuserDataSet(record);
    form.setFieldsValue(record);
    setSelectedIconName(record.icon_name || null);
    setSelectedTypeValue(record.type || null);
    setshowAddButton(false);
    document
      .querySelector('.gpProductCreate-form')
      ?.scrollIntoView({ behavior: 'smooth' });
  };

  // ─── Open create form ─────────────────────────────────────────────────────
  const createUser = () => {
    setisUserCreateEnable(true);
    setFormMode('edit');
    setuserDataSet({});
    form.resetFields();
    setSelectedIconName(null);
    setSelectedTypeValue(null);
    setshowAddButton(false);
  };

  // ─── Name change handler ─────────────────────────────────────────────────
  const handleNameChange = () => {
    form.setFields([{ name: 'name', errors: [] }]);
  };

  // ─── Render ───────────────────────────────────────────────────────────────
  return (
    <App productCategories={showCategory}>
      {contextHolder}

      <div style={{ display: 'grid', justifyContent: 'center' }}>
        {/* Header row */}
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 15 }}>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-around',
              alignItems: 'center',
              marginBottom: '10px',
              marginInline: 20,
            }}
          >
            <Title level={5} style={{ margin: 0, color: '#008385', fontSize: 20 }}>
              All categories{' '}
              <span style={{ color: 'black', fontWeight: '400' }}>
                ({AllClinicianData?.total ? AllClinicianData?.total : '00'})
              </span>
            </Title>
          </div>
        </div>

        {/* Table */}
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <Card
            style={{
              width: 937,
              margin: 0,
              padding: 0,
              borderRadius: 40,
              boxShadow: '0 4px 10px rgba(0, 0, 0, 0.15)',
            }}
          >
            <ConfigProvider
              theme={{
                components: {
                  Table: { headerBg: '#008385', headerColor: '#ffffff' },
                  Spin: { colorPrimary: '#008385' },
                },
              }}
            >
              {AllClinicianData && (
                <DndContext
                  sensors={sensors}
                  modifiers={[restrictToVerticalAxis]}
                  onDragEnd={onDragEnd}
                >
                  <SortableContext
                    items={draggedData.map((i) => i.product_category_id)}
                    strategy={verticalListSortingStrategy}
                  >
                    <Table
                      loading={loader}
                      columns={columns}
                      dataSource={draggedData}
                      pagination={false}
                      scroll={{ x: 'auto' }}
                      rowKey="product_category_id"
                      components={{ body: { row: SortableRow } }}
                      onRow={(record) => ({
                        onClick: () => handleTableRowClick(record),
                      })}
                    />
                  </SortableContext>
                </DndContext>
              )}
              {AllClinicianData && AllClinicianData?.total ? (
                <Pagination
                  align="center"
                  defaultCurrent={1}
                  defaultPageSize={20}
                  total={AllClinicianData?.total || undefined}
                  style={{ marginTop: 10 }}
                  onChange={(val) => setCurrentPage(val)}
                  itemRender={itemRender}
                />
              ) : (
                ''
              )}
            </ConfigProvider>
          </Card>
        </div>
      </div>

      {/* Create / Edit / View panel */}
      <div
        className="gpProductCreate-form"
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: '10px',
          marginTop: 20,
          justifyContent: 'center',
        }}
      >
        {/* Add category button */}
        {showAddButton && (
          <Button
            type="default"
            className="add-generic-btn"
            icon={<PlusOutlined />}
            style={{ marginTop: 20, marginLeft: '-6%', borderColor: '#008385', color: '#008385' }}
            onClick={createUser}
          >
            Add a category
          </Button>
        )}

        {/* Form card */}
        {loader === false && isUserCreateEnable && (
          <Card
            className={isViewMode ? 'view-mode-card' : ''}
            title={
              isViewMode ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <div
                    style={{
                      width: 36,
                      height: 36,
                      borderRadius: '50%',
                      background: 'linear-gradient(135deg, #008385, #00a3a5)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#fff',
                      fontWeight: 700,
                      fontSize: 14,
                      flexShrink: 0,
                      boxShadow: '0 2px 6px rgba(0,131,133,0.30)',
                    }}
                  >
                    {getInitials(userDataSet?.name)}
                  </div>
                  <span style={{ fontWeight: 700, fontSize: 16, color: '#007a7c' }}>
                    {userDataSet?.name}
                  </span>
                </div>
              ) : (
                <span style={{ textAlign: 'left', display: 'block' }}>Category</span>
              )
            }
            style={{ width: 460, margin: '0', marginTop: 20 }}
          >
            {userDataSet !== undefined && isUserCreateEnable && loader === false && (
              <Form
                form={form}
                autoComplete="off"
                layout="vertical"
                initialValues={userDataSet || null}
                onFinish={(values) =>
                  onFinish(
                    values,
                    userDataSet &&
                      userDataSet.product_category_id &&
                      Object.keys(userDataSet).length
                      ? true
                      : false,
                  )
                }
              >
                {isViewMode ? (
                  /* ── VIEW MODE ─────────────────────────────────────────── */
                  <div style={{ padding: '0 4px', textAlign: 'left' }}>
                    <div
                      style={{
                        fontSize: 11,
                        fontWeight: 700,
                        color: '#008385',
                        letterSpacing: 1,
                        textTransform: 'uppercase',
                        textAlign: 'left',
                        marginBottom: 8,
                        paddingBottom: 4,
                        borderBottom: '1px solid #c5e8e8',
                      }}
                    >
                      Category Info
                    </div>

                    <div className="view-detail-row">
                      <label>Name</label>
                      <span className="view-field-value">
                        {userDataSet?.name || '-'}
                      </span>
                    </div>

                    <div className="view-detail-row">
                      <label>Type</label>
                      <span className="view-field-value">
                        <TypeTag type={userDataSet?.type} />
                      </span>
                    </div>

                    <div className="view-detail-row">
                      <label>Icon</label>
                      <span className="view-field-value">
                        {userDataSet?.icon_name ? (
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <div style={{
                              width: 36, height: 36, borderRadius: 8,
                              background: 'rgba(0,131,133,0.08)',
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              border: '1px solid rgba(0,131,133,0.2)',
                            }}>
                              {renderDynamicIcon(userDataSet.icon_name, { fontSize: 20, color: '#008385' })}
                            </div>
                            <span style={{ fontSize: 12, color: '#6b7280' }}>{userDataSet.icon_name}</span>
                          </div>
                        ) : '—'}
                      </span>
                    </div>

                    <div className="view-detail-row">
                      <label>Description</label>
                      <span className="view-field-value">
                        {userDataSet?.description || '-'}
                      </span>
                    </div>

                    <div className="view-detail-row">
                      <label>Status</label>
                      <span className="view-field-value">
                        {(() => {
                          const isActive = userDataSet?.status == 1 || userDataSet?.status === true;
                          return (
                            <Tag
                              icon={<PoweroffOutlined />}
                              style={{
                                background: isActive ? 'rgba(0,131,133,0.10)' : 'rgba(156,163,175,0.15)',
                                borderColor: isActive ? '#008385' : '#d1d5db',
                                color: isActive ? '#008385' : '#6b7280',
                                borderRadius: 20,
                                fontWeight: 600,
                                fontSize: 12,
                                padding: '2px 10px',
                              }}
                            >
                              {isActive ? 'Active' : 'Inactive'}
                            </Tag>
                          );
                        })()}
                      </span>
                    </div>

                    <div style={{ display: 'flex', gap: 12, marginTop: 20, paddingBottom: 4, justifyContent: 'center' }}>
                      <Button
                        htmlType="button"
                        onClick={() => {
                          setisUserCreateEnable(false);
                          setshowAddButton(true);
                          setFormMode('edit');
                        }}
                        style={{ borderColor: '#008385', color: '#008385', minWidth: 100 }}
                      >
                        Cancel
                      </Button>
                      <Button
                        htmlType="button"
                        className="edit-btn-green"
                        icon={<EditOutlined />}
                        onClick={() => setFormMode('edit')}
                        style={{ minWidth: 100 }}
                      >
                        Edit
                      </Button>
                    </div>
                  </div>
                ) : (
                  /* ── EDIT / CREATE MODE ─────────────────────────────────── */
                  <>
                    {/* Name */}
                    <Row>
                      <Col span={4}>
                        <Space.Compact style={{ width: '100%' }}>
                          <label style={{ marginTop: 5, marginRight: 5 }}>
                            Name:{' '}
                          </label>
                        </Space.Compact>
                      </Col>
                      <Col span={20}>
                        <Form.Item
                          name="name"
                          rules={[
                            { required: true, message: 'Product Name is required' },
                          ]}
                        >
                          <Input
                            placeholder="Product Category Name"
                            style={{ width: '100%', marginLeft: 10 }}
                            onChange={handleNameChange}
                            onFocus={() => form.setFields([{ name: 'name', errors: [] }])}
                          />
                        </Form.Item>
                      </Col>
                    </Row>

                    {/* Type */}
                    <Row>
                      <Col span={4}>
                        <Space.Compact style={{ display: 'flex' }}>
                          <label style={{ marginTop: 5, marginRight: 5 }}>
                            Type:{' '}
                          </label>
                        </Space.Compact>
                      </Col>
                      <Col span={20}>
                        <Form.Item
                          name="type"
                          rules={[
                            { required: true, message: 'Category Type is required' },
                          ]}
                        >
                          <Select
                            placeholder="Select Category Type"
                            style={{ width: '100%', marginLeft: 10 }}
                            allowClear
                            onChange={(val) => setSelectedTypeValue(val ?? null)}
                          >
                            {categoryTypeMetaData.length > 0 ? categoryTypeMetaData
                              .filter((meta) => meta.meta_value !== selectedTypeValue && meta.meta_value !== 'gp')
                              .map((meta) => {
                                const cfg = getCategoryTypeConfig(meta.meta_value);
                                return (
                                  <Option key={meta.meta_value} value={meta.meta_value}>
                                    <span
                                      style={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: 6,
                                        color: cfg.color,
                                      }}
                                    >
                                      {cfg.icon} {meta.meta_label}
                                    </span>
                                  </Option>
                                );
                              }) : CATEGORY_TYPE_OPTIONS
                                .filter((opt) => opt.value !== selectedTypeValue && opt.value !== 'gp')
                                .map((opt) => (
                                  <Option key={opt.value} value={opt.value}>
                                    <span
                                      style={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: 6,
                                        color: opt.color,
                                      }}
                                    >
                                      {opt.icon} {opt.label}
                                    </span>
                                  </Option>
                                ))}
                          </Select>
                        </Form.Item>
                      </Col>
                    </Row>

                    {/* Icon picker */}
                    <Row style={{ marginBottom: 8 }}>
                      <Col span={4}>
                        <Space.Compact style={{ display: 'flex' }}>
                          <label style={{ marginTop: 5, marginRight: 5 }}>Icon: </label>
                        </Space.Compact>
                      </Col>
                      <Col span={20}>
                        {/* Hidden form field stores icon_name string */}
                        <Form.Item name="icon_name" hidden><Input /></Form.Item>
                        <div style={{ marginLeft: 10, display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                          {/* Preview */}
                          {selectedIconName ? (
                            <div style={{
                              width: 38, height: 38, borderRadius: 8,
                              background: 'rgba(0,131,133,0.08)',
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              border: '1px solid rgba(0,131,133,0.25)',
                            }}>
                              {renderDynamicIcon(selectedIconName, { fontSize: 20, color: '#008385' })}
                            </div>
                          ) : (
                            <div style={{
                              width: 38, height: 38, borderRadius: 8,
                              background: '#f5f5f5', border: '1px dashed #d1d5db',
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              color: '#d1d5db', fontSize: 18,
                            }}>
                              <AppstoreOutlined />
                            </div>
                          )}
                          {/* Choose button */}
                          <Button
                            size="small"
                            style={{ borderColor: '#008385', color: '#008385' }}
                            onClick={() => setIconPickerOpen(true)}
                          >
                            {selectedIconName ? 'Change Icon' : 'Choose Icon'}
                          </Button>
                          {/* Clear */}
                          {selectedIconName && (
                            <Button
                              size="small"
                              type="text"
                              danger
                              icon={<CloseOutlined />}
                              onClick={() => {
                                setSelectedIconName(null);
                                form.setFieldValue('icon_name', null);
                              }}
                            />
                          )}
                        </div>
                      </Col>
                    </Row>

                    {/* Status toggle */}
                    <Row style={{ marginBottom: 8 }}>
                      <Col span={4}>
                        <Space.Compact style={{ display: 'flex' }}>
                          <label style={{ marginTop: 5, marginRight: 5 }}>Status: </label>
                        </Space.Compact>
                      </Col>
                      <Col span={20}>
                        <Form.Item name="status" valuePropName="checked" initialValue={true}>
                          <Switch
                            checkedChildren="Active"
                            unCheckedChildren="Inactive"
                            style={{ backgroundColor: form.getFieldValue('status') !== false ? '#008385' : '#d9d9d9', marginLeft: 10 }}
                          />
                        </Form.Item>
                      </Col>
                    </Row>

                    {/* Description */}
                    <Row>
                      <Col span={4}>
                        <Space.Compact style={{ display: 'flex' }}>
                          <label style={{ marginTop: 5, marginRight: 5 }}>
                            Description:{' '}
                          </label>
                        </Space.Compact>
                      </Col>
                      <Col span={20}>
                        <Form.Item
                          name="description"
                        >
                          <Input
                            placeholder="Product Description"
                            style={{ width: '100%', marginLeft: 10 }}
                          />
                        </Form.Item>
                      </Col>
                    </Row>

                    {/* Action buttons */}
                    <>
                      <Button
                        htmlType="button"
                        className="add-generic-btn"
                        style={{
                          backgroundColor: '#008385',
                          color: '#fff',
                          marginRight: 10,
                        }}
                        onClick={() => {
                          form.resetFields();
                          setSelectedIconName(null);
                          setisUserCreateEnable(false);
                          setshowAddButton(true);
                          setFormMode('edit');
                        }}
                      >
                        Cancel
                      </Button>
                      <Button
                        style={{
                          backgroundColor: isSubmitting ? '#80bebe' : '#008385',
                          borderColor: isSubmitting ? '#80bebe' : '#008385',
                          color: '#fff',
                          cursor: isSubmitting ? 'not-allowed' : 'pointer',
                        }}
                        htmlType="submit"
                        loading={isSubmitting}
                        disabled={isSubmitting}
                      >
                        {isSubmitting ? 'Saving...' : 'Save'}
                      </Button>
                    </>
                  </>
                )}
              </Form>
            )}
          </Card>
        )}
      </div>

      {/* Icon Picker Modal */}
      <IconPickerModal
        open={iconPickerOpen}
        onClose={() => setIconPickerOpen(false)}
        value={selectedIconName}
        onSelect={(name) => {
          setSelectedIconName(name);
          form.setFieldValue('icon_name', name);
        }}
      />
    </App>
  );
};

export default NewProductCategory;
